import React, { useState, useEffect } from "react";
import classNames from "classnames";
import {
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { ShipmentDetailModal } from "./expedition-modals";

import { ShipmentItem, StageKey, LogiStatus, PAY_STYLE, LOGI_STYLE, MODE_STYLE, money, kg, toFloat } from "./envois-shared";
import EnvoisKanban from "./envois-kanban";

import {
  listInternationalExpeditions,
  initiatePayment,
} from "../../../services/crm/clementino-service";
import {
  ClementinoExpedition,
  FiltresExpeditions,
  normalizeStatus,
} from "../../../models/crm/clementino/clementino-types";
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/24/outline";

export const mapClementinoToLogi = (st?: any): LogiStatus => {
  const s = normalizeStatus(st).toUpperCase();
  if (!s) return "Attente dépôt";
  if (s.includes("ATTENTE") || s === "CREE") return "Attente dépôt";
  if (s.includes("RECEP") || s.includes("DEPOT")) return "Réceptionné";
  if (s.includes("EXPED")) return "Expédié";
  if (s.includes("TRANSIT") || s.includes("COURS")) return "En transit";
  if (s.includes("DOUAN")) return "Dédouanement";
  if (s.includes("CORRESPONDANT")) return "Chez le correspondant";
  if (s.includes("LIVR") || s.includes("TERMIN")) return "Livré";
  if (s.includes("ANNUL") || s.includes("REFUS")) return "Annulé";
  return "En transit";
};

export const localLogiToClementino = (st: string): string => {
  switch (st) {
    case "Attente dépôt":
      return "EN_ATTENTE_DEPOT";
    case "Réceptionné":
      return "RECEPTIONNE";
    case "Expédié":
      return "EXPEDIE";
    case "En transit":
      return "EN_TRANSIT";
    case "Dédouanement":
      return "DEDOUANEMENT";
    case "Chez le correspondant":
      return "CHEZ_CORRESPONDANT";
    case "Livré":
      return "LIVRE";
    case "Annulé":
      return "ANNULE";
    default:
      return "EN_TRANSIT";
  }
};

export const clementinoToShipmentItem = (e: ClementinoExpedition): ShipmentItem => {
  const isUrbain = e.type === "urbain" || !!e.coursier;
  const isExpress = (e.type || "").toLowerCase().includes("express");
  const isMaritime = (e.type || "").toLowerCase().includes("maritime");
  const mode = (isUrbain ? "Urbain" : isExpress ? "Express" : isMaritime ? "Maritime" : "Aérien") as ShipmentItem["mode"];

  const fromC = typeof e.paysExpedition === "object" && e.paysExpedition !== null
    ? ((e.paysExpedition as any).alpha2 || (e.paysExpedition as any).nomFr || "CM")
    : String(e.paysExpedition || "CM");
  const toC = typeof e.paysDestination === "object" && e.paysDestination !== null
    ? ((e.paysDestination as any).alpha2 || (e.paysDestination as any).nomFr || "FR")
    : String(e.paysDestination || "FR");

  const fromCity = typeof e.villeExpedition === "object" && e.villeExpedition !== null
    ? ((e.villeExpedition as any).nom || (e.villeExpedition as any).name || "DOUALA")
    : String(e.villeExpedition || "DOUALA");
  const toCity = typeof e.villeDestination === "object" && e.villeDestination !== null
    ? ((e.villeDestination as any).nom || (e.villeDestination as any).name || "PARIS")
    : String(e.villeDestination || "PARIS");

  const colisList: any[] = Array.isArray(e.colis)
    ? e.colis
    : e.colis && typeof e.colis === "object"
    ? [e.colis]
    : [];

  const numColis = colisList.length > 0
    ? colisList.reduce((s, c) => s + toFloat(c.nombre, 1), 0)
    : toFloat((e as any).nbTotalColis, 1);
  const poids = colisList.reduce((s, c) => s + toFloat(c.poidsColis ?? c.poids, 0), 0);
  const nature = colisList[0]
    ? (colisList[0].description || String(colisList[0].natureColis || "Colis standard"))
    : String((e as any).natureColis || "Colis standard");
  const montant = toFloat(e.montant, 0);
  const valeur = colisList.reduce((s, c) => s + toFloat(c.valeurMarchande ?? c.valeur, 0), 0) || montant;

  const rawStatus = (e as any).status ?? (e as any).statut;
  const logi = mapClementinoToLogi(rawStatus);
  const pay = (e.modePaiement ? "Payé" : (montant > 0 ? "Non payé" : "Payé")) as ShipmentItem["pay"];
  const paid = pay === "Payé" ? montant : 0;

  return {
    id: String(e.id),
    code: e.numLt || (e as any).numLettreTransport || (e as any).numExpedition || (e as any).numeroExpedition || `EXP-${e.id}`,
    createdBy: "Agent Clementino",
    createdAt: e.dateCreation ? new Date(e.dateCreation).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—",
    lead: "Intex Clementino",
    awb: "",
    awbBy: "",
    awbAt: "",
    sender: e.contactExpediteur || "Expéditeur inconnu",
    phone: e.telExpediteur || "—",
    receiver: e.contactDestinataire || "Destinataire inconnu",
    receiverPhone: e.telDestinataire || "—",
    from: String(fromCity).toUpperCase(),
    fromC,
    to: String(toCity).toUpperCase(),
    toC,
    corridor: `${fromC} → ${toC}`,
    mode,
    depart: e.dateCreation ? e.dateCreation.slice(0, 10) : "",
    departInfo: e.dateCreation ? `Créé le ${new Date(e.dateCreation).toLocaleDateString("fr-FR")}` : "",
    eta: e.dateLivraisonSouhaite || "—",
    colis: numColis,
    poids,
    nature,
    valeur,
    pay,
    refOM: e.numeroTelPaiement || "—",
    refTouch: "—",
    canal: e.modePaiement || "—",
    montant,
    paid,
    confirmed: pay === "Payé",
    logi,
    logiInfo: e.instructionsLivraison || "",
    sla: "Suivi logistique actif",
    slaWarn: false,
    audit: `Dossier Clementino #${e.id}`,
    note: "",
  };
};

const COMM = 0.05;

const confirmationLabel = (r: ShipmentItem) =>
  r.confirmed ? "Paiement validé" : r.pay !== "Remboursé" ? "À valider" : "Dossier clos";

const amountLabel = (r: ShipmentItem) =>
  toFloat(r.montant) - toFloat(r.paid) > 0 && r.pay !== "Remboursé" ? "Reste à payer" : "Encaissé";

type ColumnDef = {
  key: string;
  label: string;
  width: number;
  align?: "left" | "right" | "center";
  // Value used for sorting; omitted = column not sortable.
  sortValue?: (r: ShipmentItem) => string | number;
  // Text matched by the column's search box.
  searchText?: (r: ShipmentItem) => string;
  // Value matched by the column's dropdown filter (options are derived from the data).
  filterValue?: (r: ShipmentItem) => string;
};

const TABLE_COLUMNS: ColumnDef[] = [
  {
    key: "code",
    label: "Code / Date",
    width: 190,
    sortValue: (r) => r.code,
    searchText: (r) => `${r.code} ${r.createdBy} ${r.createdAt}`,
  },
  {
    key: "awb",
    label: "AWB / BL",
    width: 180,
    sortValue: (r) => r.awb || "~",
    searchText: (r) => `${r.awb || "à émettre"} ${r.awbBy}`,
  },
  {
    key: "parties",
    label: "Expéditeur & Destinataire",
    width: 250,
    sortValue: (r) => r.sender.toLowerCase(),
    searchText: (r) => `${r.sender} ${r.phone} ${r.receiver} ${r.receiverPhone}`,
  },
  {
    key: "trajet",
    label: "Trajet & Mode",
    width: 230,
    sortValue: (r) => `${r.from} ${r.to}`,
    searchText: (r) => `${r.from} ${r.to} ${r.mode} ${r.corridor}`,
  },
  {
    key: "colis",
    label: "Colis & Poids",
    width: 180,
    sortValue: (r) => r.poids,
    searchText: (r) => `${r.colis} colis ${r.nature}`,
  },
  {
    key: "paiement",
    label: "Paiement & Réf",
    width: 190,
    sortValue: (r) => r.pay,
    filterValue: (r) => r.pay,
  },
  {
    key: "montant",
    label: "Montant",
    width: 140,
    align: "right",
    sortValue: (r) => r.montant,
    filterValue: amountLabel,
  },
  {
    key: "depart",
    label: "Départ & ETA",
    width: 200,
    sortValue: (r) => r.depart,
    searchText: (r) => `${r.depart} ${r.departInfo} ${r.eta}`,
  },
  {
    key: "confirmation",
    label: "Confirmation",
    width: 150,
    sortValue: confirmationLabel,
    filterValue: confirmationLabel,
  },
  {
    key: "logi",
    label: "Statut logistique",
    width: 190,
    sortValue: (r) => r.logi,
    filterValue: (r) => r.logi,
  },
  { key: "actions", label: "Actions", width: 80, align: "center" },
];

type ColumnKey = string;

const HIDDEN_COLS_STORAGE_KEY = "envois.hiddenColumns";
const COL_WIDTHS_STORAGE_KEY = "envois.columnWidths";
const MIN_COL_WIDTH = 70;

function loadStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveStored(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

export default function EnvoisPage({ onOpenIntlWizard }: { onOpenIntlWizard?: () => void }) {
  const [data, setData] = useState<ShipmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fPaysExpedition, setFPaysExpedition] = useState("Tous");
  const [fPaysDestination, setFPaysDestination] = useState("Tous");
  const [fDateDebut, setFDateDebut] = useState("");
  const [fDateFin, setFDateFin] = useState("");
  const [view, setView] = useState<"table" | "kanban" | "grid">("table");
  const [q, setQ] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [showKpiCards, setShowKpiCards] = useState(true);
  const [isFullScreenPage, setIsFullScreenPage] = useState(false);
  const [isFullScreenTable, setIsFullScreenTable] = useState(false);
  const [modalShipment, setModalShipment] = useState<ShipmentItem | null>(null);
  const [fCorridor, setFCorridor] = useState("Tous");
  const [fMode, setFMode] = useState("Tous");
  const [fLogi, setFLogi] = useState("Tous");
  const [fPay, setFPay] = useState("Tous");
  const [fCommercial, setFCommercial] = useState("Tous");
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [hiddenCols, setHiddenCols] = useState<ColumnKey[]>(() => loadStored(HIDDEN_COLS_STORAGE_KEY, []));
  const [colsMenuOpen, setColsMenuOpen] = useState(false);
  const [colWidths, setColWidths] = useState<Record<ColumnKey, number>>(() => loadStored(COL_WIDTHS_STORAGE_KEY, {}));
  const [colFilters, setColFilters] = useState<Record<ColumnKey, string>>({});
  const [sort, setSort] = useState<{ key: ColumnKey; dir: "asc" | "desc" } | null>(null);

  const fetchInternationalData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filtres: FiltresExpeditions = {};
      if (fPaysExpedition && fPaysExpedition !== "Tous") filtres.pays_expedition = fPaysExpedition;
      if (fPaysDestination && fPaysDestination !== "Tous") filtres.pays_destination = fPaysDestination;
      if (fDateDebut) filtres.date_debut = fDateDebut;
      if (fDateFin) filtres.date_fin = fDateFin;
      if (fLogi && fLogi !== "Tous") filtres.status = localLogiToClementino(fLogi);
      if (q.trim()) filtres.search = q.trim();

      const entities = await listInternationalExpeditions(filtres);
      const items = entities.map(clementinoToShipmentItem);
      setData(items);
    } catch (err: any) {
      setError(
        err?.message ||
        "Erreur lors du chargement des expéditions internationales depuis Clementino. Vérifiez votre clé Gateway API."
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [fPaysExpedition, fPaysDestination, fDateDebut, fDateFin, fLogi, q]);

  useEffect(() => {
    fetchInternationalData();
  }, [fetchInternationalData]);

  const show = (key: ColumnKey) => !hiddenCols.includes(key);
  const toggleCol = (key: ColumnKey) => {
    const next = show(key) ? [...hiddenCols, key] : hiddenCols.filter((k) => k !== key);
    setHiddenCols(next);
    saveStored(HIDDEN_COLS_STORAGE_KEY, next);
  };

  const visibleCols = TABLE_COLUMNS.filter((c) => show(c.key));
  const colWidth = (c: ColumnDef) => colWidths[c.key] ?? c.width;
  const activeColFilters = Object.values(colFilters).filter((v) => v.trim()).length;

  // asc → desc → no sort
  const toggleSort = (key: ColumnKey) => {
    if (sort?.key !== key) setSort({ key, dir: "asc" });
    else if (sort.dir === "asc") setSort({ key, dir: "desc" });
    else setSort(null);
  };

  const startResize = (e: React.MouseEvent, col: ColumnDef) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidth(col);
    let latest = colWidths;
    const onMove = (ev: MouseEvent) => {
      const w = Math.max(MIN_COL_WIDTH, Math.round(startW + ev.clientX - startX));
      latest = { ...latest, [col.key]: w };
      setColWidths(latest);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      saveStored(COL_WIDTHS_STORAGE_KEY, latest);
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const resetColWidth = (col: ColumnDef) => {
    const next = { ...colWidths };
    delete next[col.key];
    setColWidths(next);
    saveStored(COL_WIDTHS_STORAGE_KEY, next);
  };

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2400);
  };

  const filtered = data.filter((r) => {
    if (fCorridor !== "Tous" && r.corridor !== fCorridor) return false;
    if (fMode !== "Tous" && r.mode !== fMode) return false;
    if (fLogi !== "Tous" && r.logi !== fLogi) return false;
    if (fPay !== "Tous" && r.pay !== fPay) return false;
    if (fCommercial !== "Tous" && r.createdBy !== fCommercial) return false;
    if (q.trim()) {
      const hay = [
        r.code,
        r.awb,
        r.sender,
        r.receiver,
        r.phone,
        r.receiverPhone,
        r.refOM,
        r.refTouch,
        r.from,
        r.to,
        r.corridor,
      ]
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q.trim().toLowerCase())) return false;
    }
    for (const col of TABLE_COLUMNS) {
      const f = colFilters[col.key]?.trim();
      if (!f) continue;
      if (col.filterValue && col.filterValue(r) !== f) return false;
      if (col.searchText && !col.searchText(r).toLowerCase().includes(f.toLowerCase())) return false;
    }
    return true;
  });

  const sortCol = sort && TABLE_COLUMNS.find((c) => c.key === sort.key);
  if (sort && sortCol?.sortValue) {
    const getVal = sortCol.sortValue;
    const factor = sort.dir === "asc" ? 1 : -1;
    filtered.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb), "fr");
      return cmp * factor;
    });
  }

  const filterOptions = (col: ColumnDef) =>
    col.filterValue ? Array.from(new Set(data.map(col.filterValue))).sort((a, b) => a.localeCompare(b, "fr")) : [];

  const totalKg = filtered.reduce((a, r) => a + toFloat(r.poids), 0);
  const totalPaid = filtered.reduce((a, r) => a + toFloat(r.paid), 0);
  const totalDue = filtered
    .filter((r) => r.pay !== "Remboursé")
    .reduce((a, r) => a + (toFloat(r.montant) - toFloat(r.paid)), 0);

  const selCount = Object.values(sel).filter(Boolean).length;

  const handleConfirmPay = async (id: string, code: string) => {
    const item = data.find((r) => r.id === id);
    if (!item) return;

    try {
      flash(`Initiation du paiement Mobile Money pour ${code}...`);
      const session = await initiatePayment({
        numLts: [code],
        channel: "ORANGE_MONEY",
        phoneNumber: item.phone,
      });

      setData((prev) =>
        prev.map((r) =>
          r.id === id
            ? {
                ...r,
                confirmed: true,
                pay: "Payé",
                paid: r.montant,
                refOM: session?.reference || `OCTO-${Date.now()}`,
              }
            : r
        )
      );
      flash(`Paiement Mobile Money initié avec succès · Réf: ${session?.reference || "confirmé"}`);
    } catch (err: any) {
      flash("Erreur lors de l initiation du paiement: " + (err?.message || "Erreur serveur"));
    }
    setOpenMenuId(null);
  };

  const handleStageMove = (id: string, stage: StageKey, patch: Partial<ShipmentItem>, note: string) => {
    const stamp = new Date().toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    setData((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch, stage, audit: `${r.audit} · ${stamp} ${note}` } : r))
    );
    flash(`${data.find((r) => r.id === id)?.code ?? "Expédition"} déplacée — ${note}`);
  };

  return (
    <div
      className={classNames(
        "h-full flex flex-col bg-[#faf7ff] text-[#1f1b2e] transition-all",
        // overflow-clip (not hidden) so the sticky header below keeps working
        isFullScreenPage ? "fixed inset-0 z-50 p-6 bg-[#faf7ff] overflow-hidden" : "p-6 lg:p-7 overflow-clip"
      )}
    >
      {/* Header bar — stays pinned under the app header while the page scrolls */}
      <div
        className={classNames(
          "flex items-start justify-between gap-x-6 gap-y-3 flex-wrap mb-4 shrink-0",
          !isFullScreenPage &&
            "sticky top-[50px] z-30 -mx-6 px-6 lg:-mx-7 lg:px-7 -mt-6 lg:-mt-7 pt-6 lg:pt-7 pb-3 bg-[#faf7ff] border-b border-transparent"
        )}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold tracking-tight text-[#1f1b2e]">
              Expéditions Internationales (Fret Aérien & Maritime)
            </h1>
            {/* Fullscreen Page Toggle */}
            <button
              type="button"
              onClick={() => setIsFullScreenPage(!isFullScreenPage)}
              title={isFullScreenPage ? "Quitter le plein écran de la page" : "Plein écran sur toute la page"}
              className="p-1.5 rounded-lg border border-purple-200 bg-white hover:bg-purple-50 text-purple-800 text-xs font-semibold flex items-center gap-1 shrink-0 whitespace-nowrap shadow-2xs cursor-pointer"
            >
              {isFullScreenPage ? (
                <>
                  <ArrowsPointingInIcon className="w-3.5 h-3.5" />
                  <span>Réduire</span>
                </>
              ) : (
                <>
                  <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                  <span>Plein écran</span>
                </>
              )}
            </button>
            {/* Toggle KPI Cards */}
            <button
              type="button"
              onClick={() => setShowKpiCards(!showKpiCards)}
              title={showKpiCards ? "Masquer les statistiques" : "Afficher les statistiques"}
              className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-semibold flex items-center gap-1 shrink-0 whitespace-nowrap shadow-2xs cursor-pointer"
            >
              {showKpiCards ? (
                <>
                  <EyeSlashIcon className="w-3.5 h-3.5" />
                  <span>Masquer stats</span>
                </>
              ) : (
                <>
                  <EyeIcon className="w-3.5 h-3.5" />
                  <span>Afficher stats</span>
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-[#6b6480] mt-1 font-medium">
            {filtered.length} expéditions sur {data.length} · {data.filter((r) => !r.confirmed && r.pay !== "Remboursé").length} paiements à confirmer · {data.filter((r) => r.logi === "Dédouanement").length} en douane
          </p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            type="button"
            onClick={fetchInternationalData}
            disabled={loading}
            className="flex items-center gap-1.5 h-9 px-3.5 border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-900 rounded-lg text-xs font-semibold cursor-pointer shadow-xs disabled:opacity-50"
          >
            <ArrowPathIcon className={classNames("w-3.5 h-3.5", loading && "animate-spin")} />
            <span>Actualiser Clementino</span>
          </button>
          <button
            type="button"
            onClick={() => flash(`Export Excel généré (${filtered.length} lignes)`)}
            className="flex items-center gap-1.5 h-9 px-3.5 border border-slate-300 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-700 transition-colors shadow-xs cursor-pointer"
          >
            <span className="w-2 h-2 rounded-xs bg-emerald-600" />
            Export Excel
          </button>
          <button
            type="button"
            onClick={() => flash(`Export PDF généré (${filtered.length} lignes)`)}
            className="flex items-center gap-1.5 h-9 px-3.5 border border-slate-300 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-700 transition-colors shadow-xs cursor-pointer"
          >
            <span className="w-2 h-2 rounded-xs bg-rose-600" />
            Export PDF
          </button>
          <button
            type="button"
            onClick={() => flash("Synchronisation des suivis transporteurs lancée")}
            className="h-9 px-3.5 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-lg text-xs font-semibold text-blue-700 transition-colors shadow-xs cursor-pointer"
          >
            Sync. suivi transporteurs
          </button>
        </div>
      </div>

      {/* KPI Cards (Collapsible) */}
      {showKpiCards && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3.5 shrink-0 animate-fade-in">
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              Prospects / devis
            </div>
            <div className="text-xl font-bold text-rose-700 mt-1">
              {data.filter((r) => r.pay === "Non payé").length}
            </div>
            <div className="text-[11px] text-[#9b93b3]">saisis, non payés</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              Paiements à valider
            </div>
            <div className="text-xl font-bold text-amber-700 mt-1">
              {data.filter((r) => !r.confirmed && (r.pay === "Payé" || r.pay === "Partiel")).length}
            </div>
            <div className="text-[11px] text-[#9b93b3]">réf. reçue, non validée</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              En transit / douane
            </div>
            <div className="text-xl font-bold text-sky-700 mt-1">
              {data.filter((r) => r.logi === "En transit" || r.logi === "Dédouanement").length}
            </div>
            <div className="text-[11px] text-[#9b93b3]">suivi transporteur actif</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              Poids total saisi
            </div>
            <div className="text-xl font-bold text-[#241f36] mt-1">
              {kg(totalKg)}
            </div>
            <div className="text-[11px] text-[#9b93b3]">capacité engagée</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              Commissions com.
            </div>
            <div className="text-xl font-bold text-purple-700 mt-1">
              {money(totalPaid * COMM)}
            </div>
            <div className="text-[11px] text-[#9b93b3]">5 % sur total encaissé</div>
          </div>
        </div>
      )}

      {/* View Switcher & Search Bar */}
      <div className="flex items-center gap-3 flex-wrap mb-3 shrink-0">
        <div className="flex p-0.5 bg-[#f1ecfe] rounded-lg gap-1">
          <button
            type="button"
            onClick={() => setView("table")}
            className={classNames(
              "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
              view === "table" ? "bg-white text-purple-900 shadow-xs" : "text-[#7c729b] hover:text-purple-900"
            )}
          >
            Tableau
          </button>
          <button
            type="button"
            onClick={() => setView("kanban")}
            className={classNames(
              "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
              view === "kanban" ? "bg-white text-purple-900 shadow-xs" : "text-[#7c729b] hover:text-purple-900"
            )}
          >
            Kanban statut
          </button>
          <button
            type="button"
            onClick={() => setView("grid")}
            className={classNames(
              "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
              view === "grid" ? "bg-white text-purple-900 shadow-xs" : "text-[#7c729b] hover:text-purple-900"
            )}
          >
            Grille
          </button>
        </div>

        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Code, n° AWB/BL, expéditeur, destinataire, tél, réf..."
          className="flex-1 min-w-[260px] h-9 px-3.5 border border-purple-200/90 rounded-lg bg-white text-xs text-[#1f1b2e] focus:outline-none focus:ring-2 focus:ring-purple-400"
        />

        <button
          type="button"
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="h-9 px-3.5 border border-purple-200 bg-purple-50 hover:bg-purple-100 rounded-lg text-xs font-semibold text-purple-900 transition-colors cursor-pointer"
        >
          {filtersOpen ? "Masquer les filtres" : "Filtres"}
        </button>
      </div>

      {/* Filter panel */}
      {filtersOpen && (
        <div className="p-3.5 bg-white border border-[#ede9fe] rounded-xl mb-3 shrink-0 shadow-xs">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Pays Origine</span>
              <input
                type="text"
                placeholder="Ex. CM"
                value={fPaysExpedition === "Tous" ? "" : fPaysExpedition}
                onChange={(e) => setFPaysExpedition(e.target.value.toUpperCase() || "Tous")}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white uppercase font-mono"
              />
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Pays Destination</span>
              <input
                type="text"
                placeholder="Ex. FR, BE, CA"
                value={fPaysDestination === "Tous" ? "" : fPaysDestination}
                onChange={(e) => setFPaysDestination(e.target.value.toUpperCase() || "Tous")}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white uppercase font-mono"
              />
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Période début</span>
              <input
                type="date"
                value={fDateDebut}
                onChange={(e) => setFDateDebut(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              />
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Période fin</span>
              <input
                type="date"
                value={fDateFin}
                onChange={(e) => setFDateFin(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              />
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Corridor</span>
              <select
                value={fCorridor}
                onChange={(e) => setFCorridor(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="Tous">Tous les corridors</option>
                <option value="Cameroun → France">Cameroun → France</option>
                <option value="Cameroun → Belgique">Cameroun → Belgique</option>
                <option value="Cameroun → Canada">Cameroun → Canada</option>
                <option value="Cameroun → États-Unis">Cameroun → États-Unis</option>
                <option value="Cameroun → Allemagne">Cameroun → Allemagne</option>
                <option value="Cameroun → Côte d'Ivoire">Cameroun → Côte d&apos;Ivoire</option>
                <option value="Cameroun → Congo">Cameroun → Congo</option>
                <option value="Cameroun → Pays-Bas">Cameroun → Pays-Bas</option>
                <option value="France → Cameroun">France → Cameroun</option>
                <option value="Livraison urbaine Douala">Livraison urbaine Douala</option>
              </select>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Mode</span>
              <select
                value={fMode}
                onChange={(e) => setFMode(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="Tous">Tous les modes</option>
                <option value="Aérien">Aérien</option>
                <option value="Maritime">Maritime</option>
                <option value="Express">Express</option>
                <option value="Urbain">Urbain</option>
              </select>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Statut logistique</span>
              <select
                value={fLogi}
                onChange={(e) => setFLogi(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="Tous">Tous statuts</option>
                <option value="Attente dépôt">Attente dépôt</option>
                <option value="Réceptionné">Réceptionné</option>
                <option value="En transit">En transit</option>
                <option value="Dédouanement">Dédouanement</option>
                <option value="Livré">Livré</option>
                <option value="Annulé">Annulé</option>
              </select>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Statut paiement</span>
              <select
                value={fPay}
                onChange={(e) => setFPay(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="Tous">Tous paiements</option>
                <option value="Payé">Payé</option>
                <option value="Non payé">Non payé</option>
                <option value="Partiel">Partiel</option>
                <option value="Remboursé">Remboursé</option>
              </select>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Commercial</span>
              <select
                value={fCommercial}
                onChange={(e) => setFCommercial(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="Tous">Tous commerciaux</option>
                <option value="Mireille N.">Mireille N.</option>
                <option value="Kevin M.">Kevin M.</option>
                <option value="Ange T.">Ange T.</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Selected bulk action bar if any */}
      {selCount > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 bg-[#1f1b2e] rounded-xl text-white text-xs mb-3 shrink-0">
          <span className="font-bold">{selCount} expédition(s) sélectionnée(s)</span>
          <div className="h-4 w-px bg-gray-600" />
          <button
            type="button"
            onClick={() => flash(`Paiements confirmés pour ${selCount} envoi(s)`)}
            className="px-2.5 py-1 bg-purple-700 hover:bg-purple-800 rounded text-xs font-semibold cursor-pointer"
          >
            Confirmer les paiements
          </button>
          <button
            type="button"
            onClick={() => flash(`Manifeste généré pour ${selCount} envoi(s)`)}
            className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs font-semibold cursor-pointer"
          >
            Générer manifeste
          </button>
          <button
            type="button"
            onClick={() => setSel({})}
            className="ml-auto text-gray-400 hover:text-white cursor-pointer text-xs"
          >
            Désélectionner
          </button>
        </div>
      )}

      {error && (
        <div className="mb-3 p-3.5 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-rose-600 shrink-0" />
            <span>{error}</span>
          </div>
          <button
            type="button"
            onClick={fetchInternationalData}
            className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-lg text-xs"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Content views with independent internal scroll */}
      <div
        className={classNames(
          "flex-1 min-h-0 overflow-hidden flex flex-col bg-white border border-[#ece7f8] rounded-xl shadow-xs transition-all",
          isFullScreenTable ? "fixed inset-0 z-50 p-6 bg-[#faf7ff]" : ""
        )}
      >
        {/* Table / view header bar with fullscreen table zoom toggle */}
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#fbf9ff] border-b border-[#ece7f8] text-xs whitespace-nowrap">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-bold text-[#1f1b2e]">Liste des expéditions</span>
            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full font-bold text-[11px]">
              {filtered.length} lignes
            </span>
            <span className="text-[11px] text-gray-500 hidden 2xl:inline truncate">
              (Cliquez sur une ligne pour ouvrir le détail)
            </span>
          </div>

          <div className="flex items-center gap-2">
            {view === "table" && (activeColFilters > 0 || sort) && (
              <button
                type="button"
                onClick={() => {
                  setColFilters({});
                  setSort(null);
                }}
                className="px-2.5 py-1 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-md border border-rose-200 cursor-pointer"
              >
                Effacer filtres & tri{activeColFilters > 0 ? ` (${activeColFilters})` : ""}
              </button>
            )}
            {view === "table" && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setColsMenuOpen(!colsMenuOpen)}
                  title="Afficher / masquer des colonnes"
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 rounded-md border border-gray-200 transition-colors cursor-pointer"
                >
                  <EyeIcon className="w-3.5 h-3.5" />
                  <span>Colonnes</span>
                  {hiddenCols.length > 0 && (
                    <span className="px-1.5 bg-purple-100 text-purple-800 rounded-full text-[10px] font-bold">
                      {TABLE_COLUMNS.length - hiddenCols.length}/{TABLE_COLUMNS.length}
                    </span>
                  )}
                </button>
                {colsMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setColsMenuOpen(false)} />
                    <div className="absolute right-0 top-8 z-40 w-60 bg-white border border-gray-200 rounded-xl shadow-xl p-1.5">
                      {TABLE_COLUMNS.map((col) => (
                        <label
                          key={col.key}
                          className="flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-purple-50 text-xs text-gray-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={show(col.key)}
                            onChange={() => toggleCol(col.key)}
                            className="w-3.5 h-3.5 accent-purple-700"
                          />
                          {col.label}
                        </label>
                      ))}
                      {hiddenCols.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setHiddenCols([]);
                            try {
                              localStorage.removeItem(HIDDEN_COLS_STORAGE_KEY);
                            } catch {}
                          }}
                          className="w-full mt-1 px-2.5 py-1.5 border-t border-gray-100 text-left text-xs font-semibold text-purple-700 hover:bg-purple-50 rounded"
                        >
                          Tout afficher
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {onOpenIntlWizard && (
              <button
                type="button"
                onClick={onOpenIntlWizard}
                className="px-2.5 py-1 bg-[#6d28d9] hover:bg-purple-800 text-white rounded-md text-xs font-bold shadow-2xs"
              >
                + Nouvelle expédition
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsFullScreenTable(!isFullScreenTable)}
              title={isFullScreenTable ? "Quitter le plein écran du tableau" : "Zoom plein écran sur le tableau uniquement"}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md border border-purple-200 transition-colors cursor-pointer"
            >
              {isFullScreenTable ? (
                <>
                  <ArrowsPointingInIcon className="w-3.5 h-3.5" />
                  <span>Réduire tableau</span>
                </>
              ) : (
                <>
                  <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                  <span>Plein écran</span>
                </>
              )}
            </button>
          </div>
        </div>

        {view === "table" && (
          <div className="flex-1 overflow-auto tracking-scrollbar">
            <table
              className="table-fixed min-w-full border-separate border-spacing-0 text-xs"
              style={{ width: 40 + visibleCols.reduce((sum, c) => sum + colWidth(c), 0) }}
            >
              <colgroup>
                <col style={{ width: 40 }} />
                {visibleCols.map((c) => (
                  <col key={c.key} style={{ width: colWidth(c) }} />
                ))}
              </colgroup>
              <thead className="sticky top-0 bg-[#f8f6fc] z-20 shadow-2xs">
                <tr className="text-left text-[11px] font-bold text-gray-600 tracking-wider uppercase whitespace-nowrap">
                  <th className="p-3 sticky left-0 z-10 bg-[#f8f6fc]">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && filtered.every((r) => sel[r.id])}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const newSel: Record<string, boolean> = {};
                        filtered.forEach((r) => {
                          newSel[r.id] = checked;
                        });
                        setSel(newSel);
                      }}
                      className="w-3.5 h-3.5 accent-purple-700"
                    />
                  </th>
                  {visibleCols.map((col) => {
                    const sorted = sort?.key === col.key ? sort.dir : null;
                    return (
                      <th
                        key={col.key}
                        className={classNames(
                          "p-0 bg-[#f8f6fc]",
                          col.key === "code" && "sticky left-10 z-10 shadow-[inset_-1px_0_0_#ece7f8]",
                          col.key !== "code" && "relative"
                        )}
                        aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : undefined}
                      >
                        {col.sortValue ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(col.key)}
                            title="Trier"
                            className={classNames(
                              "w-full flex items-center gap-1 p-3 pr-4 uppercase tracking-wider cursor-pointer hover:text-purple-800",
                              col.align === "right" && "justify-end",
                              sorted && "text-purple-800"
                            )}
                          >
                            <span className="truncate">{col.label}</span>
                            {sorted === "asc" ? (
                              <ChevronUpIcon className="w-3 h-3 shrink-0" />
                            ) : sorted === "desc" ? (
                              <ChevronDownIcon className="w-3 h-3 shrink-0" />
                            ) : (
                              <ChevronUpDownIcon className="w-3 h-3 shrink-0 text-gray-300" />
                            )}
                          </button>
                        ) : (
                          <div className={classNames("p-3 truncate", col.align === "center" && "text-center")}>{col.label}</div>
                        )}
                        {/* Resize handle — drag to resize, double-click to reset */}
                        <div
                          onMouseDown={(e) => startResize(e, col)}
                          onDoubleClick={() => resetColWidth(col)}
                          title="Glisser pour redimensionner · double-clic pour réinitialiser"
                          className="absolute top-0 right-0 h-full w-2 cursor-col-resize group/resize flex justify-center"
                        >
                          <div className="w-px h-full bg-purple-100 group-hover/resize:bg-purple-400 group-hover/resize:w-0.5" />
                        </div>
                      </th>
                    );
                  })}
                </tr>
                {/* Per-column search / filter row */}
                <tr className="[&>th]:border-b [&>th]:border-purple-100 [&>th]:bg-[#f8f6fc]">
                  <th className="px-3 pb-2 sticky left-0 z-10" />
                  {visibleCols.map((col) => (
                    <th
                      key={col.key}
                      className={classNames(
                        "px-2 pb-2 font-normal",
                        col.key === "code" && "sticky left-10 z-10 shadow-[inset_-1px_0_0_#ece7f8]"
                      )}
                    >
                      {col.filterValue ? (
                        <select
                          value={colFilters[col.key] ?? ""}
                          onChange={(e) => setColFilters({ ...colFilters, [col.key]: e.target.value })}
                          className={classNames(
                            "w-full h-7 px-1.5 rounded-md border text-[11px] bg-white focus:outline-none focus:ring-2 focus:ring-purple-300",
                            colFilters[col.key] ? "border-purple-400 text-purple-900 font-semibold" : "border-gray-200 text-gray-500"
                          )}
                        >
                          <option value="">Tous</option>
                          {filterOptions(col).map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      ) : col.searchText ? (
                        <div className="relative">
                          <MagnifyingGlassIcon className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                          <input
                            type="text"
                            value={colFilters[col.key] ?? ""}
                            onChange={(e) => setColFilters({ ...colFilters, [col.key]: e.target.value })}
                            placeholder="Rechercher…"
                            className={classNames(
                              "w-full h-7 pl-6 pr-1.5 rounded-md border text-[11px] bg-white font-normal normal-case focus:outline-none focus:ring-2 focus:ring-purple-300",
                              colFilters[col.key] ? "border-purple-400 text-purple-900" : "border-gray-200"
                            )}
                          />
                        </div>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const P = PAY_STYLE[r.pay];
                  const L = LOGI_STYLE[r.logi];
                  const M = MODE_STYLE[r.mode];
                  const due = r.montant - r.paid;
                  // Sticky cells need an opaque background matching the row state.
                  const stickyBg = sel[r.id] ? "bg-[#fcf9ff]" : "bg-white group-hover:bg-[#faf8ff]";

                  return (
                    <tr
                      key={r.id}
                      onClick={() => setModalShipment(r)}
                      className={classNames(
                        "group align-top hover:bg-[#faf8ff] transition-colors cursor-pointer [&>td]:border-b [&>td]:border-gray-100 [&>td]:overflow-hidden [&>td]:text-ellipsis",
                        sel[r.id] ? "bg-[#fcf9ff]" : ""
                      )}
                    >
                      <td className={classNames("p-3 sticky left-0 z-[5]", stickyBg)} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!sel[r.id]}
                          onChange={(e) =>
                            setSel({ ...sel, [r.id]: e.target.checked })
                          }
                          className="w-3.5 h-3.5 accent-purple-700"
                        />
                      </td>
                      {show("code") && (
                      <td className={classNames("p-3 whitespace-nowrap sticky left-10 z-[5] shadow-[inset_-1px_0_0_#ece7f8]", stickyBg)}>
                        <span className="font-mono font-semibold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded">
                          {r.code}
                        </span>
                        <div className="text-[11px] text-gray-500 mt-1">
                          {r.createdBy} · {r.createdAt.slice(0, 16)}
                        </div>
                      </td>
                      )}
                      {show("awb") && (
                      <td className="p-3 whitespace-nowrap">
                        <span
                          className="font-mono text-[11px] px-2 py-0.5 rounded"
                          style={{
                            backgroundColor: r.awb ? "#fff7ed" : "#f4f4f5",
                            color: r.awb ? "#c2410c" : "#a1a1aa",
                          }}
                        >
                          {r.awb || "à émettre"}
                        </span>
                        {r.awbAt && (
                          <div className="text-[10px] text-gray-400 mt-1">
                            {r.awbBy} · {r.awbAt.slice(0, 16)}
                          </div>
                        )}
                      </td>
                      )}
                      {show("parties") && (
                      <td className="p-3">
                        <div className="font-semibold text-gray-900 whitespace-nowrap">{r.sender}</div>
                        <div className="font-mono text-[11px] text-gray-500 whitespace-nowrap">{r.phone}</div>
                        <div className="text-[11px] text-gray-400 mt-1">
                          Dest: <span className="text-gray-700 font-medium">{r.receiver}</span>{" "}
                          <span className="whitespace-nowrap">({r.receiverPhone})</span>
                        </div>
                      </td>
                      )}
                      {show("trajet") && (
                      <td className="p-3 whitespace-nowrap">
                        <div className="flex items-center gap-1 font-medium text-gray-900">
                          <span>{r.from}</span>
                          <span className="text-gray-400">→</span>
                          <span>{r.to}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                            style={{ backgroundColor: M.bg, color: M.fg }}
                          >
                            {r.mode}
                          </span>
                          <span className="text-[11px] text-gray-500">{r.corridor}</span>
                        </div>
                      </td>
                      )}
                      {show("colis") && (
                      <td className="p-3 whitespace-nowrap">
                        <div className="font-bold text-gray-900">{kg(r.poids)}</div>
                        <div className="text-[11px] text-gray-500">
                          {r.colis} colis · {r.nature}
                        </div>
                      </td>
                      )}
                      {show("paiement") && (
                      <td className="p-3 whitespace-nowrap">
                        <span
                          className="inline-block px-2 py-0.5 rounded text-[11px] font-bold"
                          style={{ backgroundColor: P.bg, color: P.fg }}
                        >
                          {r.pay}
                        </span>
                        <div className="font-mono text-[10px] text-gray-500 mt-1">
                          {r.refOM !== "—" ? `OM: ${r.refOM}` : r.refTouch !== "—" ? `ITX: ${r.refTouch}` : ""}
                        </div>
                      </td>
                      )}
                      {show("montant") && (
                      <td className="p-3 text-right whitespace-nowrap">
                        <div className="font-bold text-gray-900">{money(r.montant)}</div>
                        <div
                          className={classNames(
                            "text-[10px] mt-0.5",
                            due > 0 && r.pay !== "Remboursé" ? "text-rose-700 font-semibold" : "text-emerald-700"
                          )}
                        >
                          {due > 0 && r.pay !== "Remboursé" ? `Reste ${money(due)}` : "Encaissé"}
                        </div>
                      </td>
                      )}
                      {show("depart") && (
                      <td className="p-3 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{r.depart}</div>
                        <div className="text-[10px] text-gray-500">{r.departInfo}</div>
                        <div className="text-[11px] text-purple-700 font-semibold mt-0.5">ETA {r.eta}</div>
                      </td>
                      )}
                      {show("confirmation") && (
                      <td className="p-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {r.confirmed ? (
                          <span className="inline-block px-2 py-0.5 bg-purple-100 rounded text-purple-800 text-[11px] font-bold">
                            Paiement validé
                          </span>
                        ) : r.pay !== "Remboursé" ? (
                          <button
                            type="button"
                            onClick={() => handleConfirmPay(r.id, r.code)}
                            className="px-2 py-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-bold rounded text-[11px] transition-colors cursor-pointer"
                          >
                            Valider paiement
                          </button>
                        ) : (
                          <span className="text-gray-400 text-[11px]">Dossier clos</span>
                        )}
                      </td>
                      )}
                      {show("logi") && (
                      <td className="p-3 whitespace-nowrap">
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-bold"
                          style={{ backgroundColor: L.bg, color: L.fg }}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: L.dot }}
                          />
                          {r.logi}
                        </span>
                        <div className="text-[10px] text-gray-500 mt-1 max-w-[140px] truncate">
                          {r.logiInfo}
                        </div>
                      </td>
                      )}
                      {show("actions") && (
                      <td className="p-3 text-center relative !overflow-visible" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => setOpenMenuId(openMenuId === r.id ? null : r.id)}
                          className="w-7 h-7 rounded border border-gray-200 hover:bg-gray-100 text-gray-600 flex items-center justify-center cursor-pointer"
                        >
                          <EllipsisHorizontalIcon className="w-4 h-4 text-gray-500" />
                        </button>
                        {openMenuId === r.id && (
                          <div className="absolute right-4 top-10 z-30 w-52 bg-white border border-gray-200 rounded-xl shadow-xl p-1 text-left">
                            <button
                              type="button"
                              onClick={() => {
                                setModalShipment(r);
                                setOpenMenuId(null);
                              }}
                              className="w-full text-left px-3 py-1.5 hover:bg-purple-50 text-xs font-semibold text-purple-900 rounded"
                            >
                              Ouvrir détails
                            </button>
                            <button
                              type="button"
                              onClick={() => handleConfirmPay(r.id, r.code)}
                              className="w-full text-left px-3 py-1.5 hover:bg-purple-50 text-xs font-semibold text-emerald-700 rounded"
                            >
                              Valider paiement
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                flash(`Bordereau / étiquette généré — ${r.code}`);
                                setOpenMenuId(null);
                              }}
                              className="w-full text-left px-3 py-1.5 hover:bg-purple-50 text-xs text-gray-700 rounded"
                            >
                              Générer étiquette
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                flash(`Message WhatsApp envoyé à ${r.sender}`);
                                setOpenMenuId(null);
                              }}
                              className="w-full text-left px-3 py-1.5 hover:bg-purple-50 text-xs text-gray-700 rounded"
                            >
                              WhatsApp expéditeur
                            </button>
                          </div>
                        )}
                      </td>
                      )}
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={visibleCols.length + 1} className="p-8 text-center text-xs text-gray-500">
                      Aucune expédition ne correspond aux filtres.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {view === "kanban" && (
          <EnvoisKanban items={filtered} onOpen={setModalShipment} onMove={handleStageMove} />
        )}

        {view === "grid" && (
          <div className="flex-1 overflow-y-auto tracking-scrollbar p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {filtered.map((r) => (
              <div
                key={r.id}
                className="p-4 bg-white border border-gray-200 rounded-xl shadow-2xs flex flex-col justify-between"
              >
                <div>
                  <div className="flex justify-between items-start">
                    <span className="font-mono font-semibold text-xs text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded">
                      {r.code}
                    </span>
                    <span
                      className="px-2 py-0.5 rounded text-[11px] font-bold"
                      style={{
                        backgroundColor: PAY_STYLE[r.pay].bg,
                        color: PAY_STYLE[r.pay].fg,
                      }}
                    >
                      {r.pay}
                    </span>
                  </div>
                  <div className="text-sm font-bold text-gray-900 mt-3">{r.sender}</div>
                  <div className="text-xs text-gray-500 mt-0.5">→ {r.receiver}</div>
                  <div className="flex items-center gap-2 mt-2.5">
                    <span className="px-2 py-0.5 bg-purple-50 text-purple-700 font-bold text-[11px] rounded">
                      {r.from}
                    </span>
                    <span className="text-gray-400">→</span>
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 font-bold text-[11px] rounded">
                      {r.to}
                    </span>
                    <span
                      className="ml-auto px-2 py-0.5 rounded text-[10px] font-bold"
                      style={{
                        backgroundColor: MODE_STYLE[r.mode].bg,
                        color: MODE_STYLE[r.mode].fg,
                      }}
                    >
                      {r.mode}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-100 text-xs">
                    <div>
                      <span className="text-[10px] text-gray-400 block uppercase font-bold">Colis</span>
                      <span>{kg(r.poids)} · {r.colis} colis</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-gray-400 block uppercase font-bold">Montant</span>
                      <span className="font-bold text-gray-900">{money(r.montant)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => handleConfirmPay(r.id, r.code)}
                    className="flex-1 h-8 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                  >
                    {r.confirmed ? "Bordereau OK" : "Valider"}
                  </button>
                  <button
                    type="button"
                    onClick={() => flash(`WhatsApp envoyé à ${r.sender}`)}
                    className="px-3 h-8 border border-gray-200 bg-white hover:bg-gray-50 text-purple-900 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                  >
                    Contacter
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table footer with stats */}
        <div className="flex items-center justify-between gap-4 flex-wrap px-4 py-2.5 bg-[#fbf9ff] border-t border-[#ece7f8] text-xs text-gray-600 shrink-0">
          <div>{filtered.length} envois affichés</div>
          <div className="flex items-center gap-4 flex-wrap">
            <span>
              Poids total: <strong className="text-gray-900">{kg(totalKg)}</strong>
            </span>
            <span>
              Encaissé: <strong className="text-emerald-700">{money(totalPaid)}</strong>
            </span>
            <span>
              Reste à percevoir: <strong className="text-rose-700">{money(totalDue)}</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed left-1/2 bottom-6 -translate-x-1/2 z-50 px-4 py-2 bg-[#1f1b2e] rounded-lg text-xs font-semibold text-white shadow-xl animate-fade-in">
          {toast}
        </div>
      )}

      {/* Shipment Detail Modal */}
      {modalShipment && (
        <ShipmentDetailModal
          shipment={modalShipment}
          onClose={() => setModalShipment(null)}
          onConfirmPay={handleConfirmPay}
          onFlash={flash}
        />
      )}
    </div>
  );
}
